import { forwardRef, useState } from "react";
import { Check, CheckCheck, AlertCircle, Bot, User as UserIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import type { Message, MessageAttachment } from "@/modules/chatbot/types";

/**
 * MessageBubble — INCOMING / OUTGOING xabar.
 * - Attachment preview (image/video/audio/file)
 * - Timestamp (HH:mm)
 * - Delivery status (SENT / DELIVERED / READ / FAILED)
 * - sentBy: AI ikon, OPERATOR avatar
 */

export interface MessageBubbleProps
  extends React.HTMLAttributes<HTMLDivElement> {
  message: Message;
  /** Attachment bosilganda lightbox uchun */
  onAttachmentClick?: (attachment: MessageAttachment) => void;
  showAuthor?: boolean;
}

export const MessageBubble = forwardRef<HTMLDivElement, MessageBubbleProps>(
  ({ message, onAttachmentClick, showAuthor = true, className, ...props }, ref) => {
    const isIncoming = message.direction === "INCOMING";
    const isAi = message.sentBy === "AI";
    const isSystem = message.sentBy === "SYSTEM";
    const isFailed = message.deliveryStatus === "FAILED";

    const time = formatDate(message.sentAt ?? message.createdAt, "HH:mm");

    return (
      <div
        ref={ref}
        className={cn(
          "flex w-full",
          isIncoming ? "justify-start" : "justify-end",
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            "max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-sm",
            isIncoming &&
              "bg-[var(--message-incoming-bg)] text-[var(--message-incoming-fg)] border border-[var(--message-incoming-border)] rounded-bl-sm",
            !isIncoming &&
              !isAi &&
              "bg-[var(--message-outgoing-bg)] text-[var(--message-outgoing-fg)] rounded-br-sm",
            !isIncoming &&
              isAi &&
              "rounded-br-sm text-[var(--message-outgoing-ai-fg)]",
            isFailed && "ring-1 ring-[var(--danger)]",
          )}
          style={
            !isIncoming && isAi
              ? {
                  background: "var(--message-outgoing-ai-bg)",
                  boxShadow: "var(--message-outgoing-ai-glow)",
                }
              : undefined
          }
        >
          {showAuthor && !isIncoming && (
            <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-wider opacity-80">
              {isAi ? (
                <>
                  <Bot className="h-3 w-3" aria-hidden />
                  AI Asistent
                </>
              ) : isSystem ? (
                "Tizim"
              ) : (
                <>
                  <UserIcon className="h-3 w-3" aria-hidden />
                  {message.sentByUserName ?? "Operator"}
                </>
              )}
            </div>
          )}

          {message.content && (
            <div className="whitespace-pre-wrap break-words">{message.content}</div>
          )}

          {message.attachments && message.attachments.length > 0 && (
            <div
              className={cn(
                "mt-2 grid gap-1.5",
                message.attachments.length === 1 ? "grid-cols-1" : "grid-cols-2",
              )}
            >
              {message.attachments.map((att, i) => (
                <AttachmentPreview
                  key={i}
                  attachment={att}
                  onClick={() => onAttachmentClick?.(att)}
                />
              ))}
            </div>
          )}

          <div
            className="mt-1 flex items-center justify-end gap-1 text-[10px]"
            style={{
              color: isIncoming
                ? "var(--message-meta-incoming-fg)"
                : "var(--message-meta-fg)",
            }}
          >
            <span>{time}</span>
            {!isIncoming && (
              <DeliveryIcon status={message.deliveryStatus} failed={isFailed} />
            )}
          </div>

          {isFailed && message.errorMessage && (
            <div className="mt-1 text-[10px] text-[var(--accent-rose-fg)] flex items-center gap-1">
              <AlertCircle className="h-3 w-3" aria-hidden />
              {message.errorMessage}
            </div>
          )}
        </div>
      </div>
    );
  },
);
MessageBubble.displayName = "MessageBubble";

function DeliveryIcon({
  status,
  failed,
}: {
  status: Message["deliveryStatus"];
  failed: boolean;
}) {
  if (failed) return <AlertCircle className="h-3 w-3" aria-hidden />;
  if (status === "READ") return <CheckCheck className="h-3 w-3" aria-hidden />;
  if (status === "DELIVERED") return <CheckCheck className="h-3 w-3 opacity-60" aria-hidden />;
  return <Check className="h-3 w-3 opacity-60" aria-hidden />;
}

function AttachmentPreview({
  attachment,
  onClick,
}: {
  attachment: MessageAttachment;
  onClick?: () => void;
}) {
  const [errored, setErrored] = useState(false);

  if (attachment.type === "IMAGE" && !errored) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="block overflow-hidden rounded-lg border border-[var(--border-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-violet)]"
      >
        <img
          src={attachment.url}
          alt={attachment.filename ?? "image"}
          className="block max-h-[280px] w-full object-cover"
          loading="lazy"
          onError={() => setErrored(true)}
        />
      </button>
    );
  }

  if (attachment.type === "VIDEO" && !errored) {
    return (
      <video
        src={attachment.url}
        poster={attachment.thumbnailUrl}
        controls
        className="block max-h-[280px] w-full rounded-lg border border-[var(--border-subtle)]"
        onError={() => setErrored(true)}
      />
    );
  }

  if (attachment.type === "AUDIO" && !errored) {
    return (
      <audio
        src={attachment.url}
        controls
        className="block w-full"
        onError={() => setErrored(true)}
      />
    );
  }

  // FILE / fallback
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 py-1.5 text-xs text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]"
    >
      <span className="truncate">
        {attachment.filename ?? "Yuklab olish"}
      </span>
    </a>
  );
}
